import React from 'react';
import { Compass } from './Compass';
import { TopCards } from './TopCards';
import { ActionPanels } from './ActionPanels';
import { PositionedGroup, TopCode } from './useFullReportViewModel';
import { CareerOrientationGroupWithQuestions } from '@vsat/shared/lib/api';
import { UnifiedHeader } from '../../../components/UnifiedHeader';
import { UnifiedFooter } from '../../../components/UnifiedFooter';
import { Brain, Download, Info, Mail, RotateCcw, Sparkles } from 'lucide-react';

type HeaderProps = {
  assessmentIdDisplay: string;
  testDate: string;
  userName: string;
  userClass: string;
  userProfile?: { name?: string; class?: string } | null;
  onRestart: () => void;
  onDownloadPdf: () => void;
  onSendEmail: () => void;
};

type ReportLayoutProps = HeaderProps & {
  top3Codes: TopCode[];
  top3Scores: { score: number }[];
  remaining: {
    id: string;
    score: number;
    name: string;
    shortName: string;
    code: string;
    color: string;
    shortDesc?: string;
  }[];
  displayGroups: CareerOrientationGroupWithQuestions[];
  compassGroups: PositionedGroup[];
  selectedLevel?: 'THCS' | 'THPT';
};

export function ReportLayout({
  assessmentIdDisplay,
  testDate,
  userName,
  userClass,
  userProfile,
  onRestart,
  onDownloadPdf,
  onSendEmail,
  top3Codes,
  top3Scores,
  remaining,
  displayGroups,
  compassGroups,
  selectedLevel,
}: ReportLayoutProps) {
  return (
    <div className='min-h-screen bg-gray-50 dark:bg-background-dark text-text-light dark:text-text-dark font-body transition-colors duration-300'>
      <UnifiedHeader />

      <main className='pt-14 pb-12 flex-1'>
        <div
          data-report-card
          className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 relative py-8 md:py-12'
        >
          {/* Hero Section - Matching HTML design exactly */}
          <div className='md:col-span-12 bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-950 dark:to-indigo-950 rounded-2xl border border-purple-200 dark:border-purple-800 shadow-xl p-6 sm:p-10 mb-8 relative overflow-hidden flex flex-col justify-center min-h-[300px]'>
            <div className='absolute inset-0 bg-gradient-to-br from-purple-100/30 to-indigo-100/30 dark:from-purple-900/30 dark:to-indigo-900/30 opacity-60 pointer-events-none rounded-2xl' />

            <div className='relative z-10 text-center w-full max-w-5xl mx-auto'>
              <h1 className='text-4xl sm:text-5xl lg:text-6xl font-extrabold text-text-light dark:text-white mb-4 leading-tight'>
                Báo cáo kết quả tự đánh giá và tư vấn hướng nghiệp
              </h1>

              <p className='text-lg sm:text-xl text-subtext-light dark:text-subtext-dark mb-4 max-w-3xl mx-auto'>
                <span className='font-semibold text-text-light dark:text-text-dark'>
                  Họ và tên:
                </span>{' '}
                {userName}
                <span className='mx-3 text-gray-400 dark:text-gray-600'>•</span>
                <span className='font-semibold text-text-light dark:text-text-dark'>
                  Ngày làm bài:
                </span>{' '}
                {testDate}
              </p>

              {/* Education Level Badge */}
              {selectedLevel && (
                <div className='flex justify-center mb-4'>
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                      selectedLevel === 'THPT'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    }`}
                  >
                    <span className='material-symbols-outlined text-base'>school</span>
                    Định hướng:{' '}
                    {selectedLevel === 'THPT' ? 'Ngành học đại học (THPT)' : 'Học nghề (THCS)'}
                  </span>
                </div>
              )}

              <div className='relative max-w-4xl mx-auto mt-4'>
                <div className='relative p-6 bg-white/70 dark:bg-card-dark/70 backdrop-blur-md rounded-xl border border-gray-100/80 dark:border-gray-700/80 shadow-lg flex items-center gap-4'>
                  <Info className='text-amber-500 w-10 h-10 shrink-0' />
                  <p className='text-base sm:text-lg text-subtext-light dark:text-subtext-dark italic leading-relaxed text-left'>
                    "Báo cáo này được xây dựng bởi IEQTE, ứng dụng lý thuyết tính cách Holland để
                    giúp học sinh Việt Nam khám phá năng lực bản thân và định hướng nghề nghiệp phù
                    hợp."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Holland Compass Section - Centered Layout */}
          <div className='md:col-span-12 bg-white dark:bg-card-dark rounded-2xl p-8 lg:p-12 shadow-lg border border-gray-100 dark:border-gray-800 mb-8'>
            <div className='flex flex-col items-center text-center max-w-3xl mx-auto mb-10'>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-center gap-3'>
                <Brain className='text-amber-500 w-10 h-10' />
                Hồ sơ tính cách (Lý thuyết Holland)
              </h2>
              <p className='text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl'>
                Biểu đồ dưới đây thể hiện mức độ phù hợp của bạn với 6 nhóm tính cách theo lý thuyết
                Holland, từ đó gợi mở những định hướng nghề nghiệp tiềm năng.
              </p>
            </div>

            <div className='flex items-center justify-center'>
              <div className='w-full overflow-visible flex justify-center'>
                <Compass groups={compassGroups} />
              </div>
            </div>
          </div>

          {/* Top 3 Groups Section Header - Matching HTML design exactly */}
          <div className='md:col-span-12 flex items-center gap-2 mb-4 px-2'>
            <Sparkles className='text-amber-500 w-7 h-7 sm:w-8 sm:h-8' />
            <h2 className='text-2xl sm:text-3xl font-bold text-text-light dark:text-white'>
              Top 3 nhóm tính cách mạnh nhất
            </h2>
          </div>

          {/* Top 3 Cards Grid */}
          <div className='md:col-span-12'>
            <TopCards
              top3Codes={top3Codes}
              top3Scores={top3Scores}
              displayGroups={displayGroups}
              selectedLevel={selectedLevel}
            />
          </div>

          {/* Action Panels (Next Steps) */}
          <div className='md:col-span-12'>
            <ActionPanels onDownloadPdf={onDownloadPdf} onRestart={onRestart} />
          </div>

          {/* Disclaimer Note */}
          <div className='md:col-span-12 mt-8'>
            <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6'>
              <div className='flex items-start gap-4'>
                <Info className='text-amber-600 dark:text-amber-400 w-6 h-6 shrink-0 mt-0.5' />
                <div className='space-y-3'>
                  <p className='text-sm text-amber-900 dark:text-amber-100 leading-relaxed'>
                    <strong className='font-semibold'>Lưu ý:</strong> Kết quả trắc nghiệm nghề
                    nghiệp là một yếu tố mang tính tham khảo hữu ích và không có giá trị vĩnh viễn
                    trong quá trình hướng nghiệp chọn nghề. Để lựa chọn nghề nghiệp phù hợp với bản
                    thân, bạn cần cân nhắc dựa trên nhiều yếu tố quan trọng khác như năng lực, sở
                    thích, kỹ năng, phẩm chất; điều kiện gia đình; nhu cầu của thị trường...
                  </p>
                  <p className='text-sm text-amber-800 dark:text-amber-200 leading-relaxed font-medium'>
                    Kết hợp giữa kết quả năng lực và trắc nghiệm nghề nghiệp sẽ giúp bạn chọn ngành
                    học để có nghề chính xác hơn.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - PDF export and Restart */}
          <div className='md:col-span-12 flex flex-col sm:flex-row justify-center gap-4 pt-4'>
            <button
              className='flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-bold transition-colors shadow-lg text-base sm:text-lg'
              onClick={onDownloadPdf}
            >
              <Download className='w-5 h-5 sm:w-6 sm:h-6' />
              Tải báo cáo PDF
            </button>
            <button
              className='flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-bold transition-colors shadow-lg text-base sm:text-lg'
              onClick={onSendEmail}
            >
              <Mail className='w-5 h-5 sm:w-6 sm:h-6' />
              Gửi qua Email
            </button>
            <button
              className='flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-card-dark text-subtext-light dark:text-subtext-dark border border-gray-200 dark:border-gray-700 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-base sm:text-lg'
              onClick={onRestart}
            >
              <RotateCcw className='w-5 h-5 sm:w-6 sm:h-6' />
              Làm lại từ đầu
            </button>
          </div>
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
